import { useEffect, useRef, useState } from 'react';

import { widgetsConfigurations } from 'config/widgetConfigurations/widgetsConfigurations';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import { useQueryClient } from 'react-query';
import 'react-resizable/css/styles.css';
import { useHistory, useParams } from 'react-router-dom';
import { pageTitle as pageTitleService } from 'services/pageTitle';
import { AppRoutes } from 'utilitys/appRoutes/AppRoutes';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';

import { getReport } from 'api/endPoints/getReport';
import { useMutationDeleteComponent } from 'api/mutations/deleteComponent.mutation';
import { useMutationSetReport } from 'api/mutations/setReport.mutation';

import { ConfirmDeleteModal } from 'components/common/ConfirmDeleteModal/ConfirmDeleteModal';
import { NoPermissionToViewReport } from 'components/common/ErrorPages/NoPermissionToViewReport';
import { ReportNotFound } from 'components/common/ErrorPages/ReportNotFound';
import OverlayWithSpinner from 'components/common/OverlayWithSpinner/overlayWithSpinner';
import { BottomHeaderSection } from 'components/common/PageContainer/BottomHeaderSection';
import { HeaderWrapper } from 'components/common/PageContainer/HeaderWrapper';
import { MainContent } from 'components/common/PageContainer/MainContent';
import PageContainer from 'components/common/PageContainer/PageContainer';
import { WidgetsLibraryModal } from 'components/common/WidgetsLibraryModal/WidgetsLibraryModal';

import { IHashmap } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import {
    BreakPointMap,
    Breakpoints,
    ColumnsPerBreakpoint,
    Report,
    ScreenBreakpoints,
    WidgetSizes,
} from 'components/Dashboards/Reporting/reporting.types';
import {
    compactSingleLayout,
    getNewGridLayoutFromSingleLayout,
    prepareReportPayloadForSetReportAPI,
    sameLayoutArray,
    translateWidgetSize,
} from 'components/Dashboards/Reporting/reporting.utils';
import { reportingConfig } from 'components/Dashboards/Reporting/reportingConfig';
import { WidgetInReport } from 'components/Dashboards/Reporting/widgetInReport/WidgetInReport';
import { ReportCardRoutingModal } from 'components/pages/ReportPage/ReportCardRoutingModal';
import classes from 'components/pages/ReportPage/ReportPage.module.scss';
import { useReportPageProvider } from 'components/pages/ReportPage/ReportPage.provider';
import { EmptyState } from 'components/pages/ReportPage/components/EmptyState/EmptyState';
import { ExportProgressModal } from 'components/pages/ReportPage/components/ExportProgressModal/ExportProgressModal';
import { ReportPageHeader } from 'components/pages/ReportPage/components/ReportPageHeader/ReportPageHeader';
import { useEdgeAutoScroll } from 'components/pages/ReportPage/hooks/useAutoSCrollOnDrag';
import { useExportReportToPdf } from 'components/pages/ReportPage/hooks/useExportReportToPdf/useExportReportToPdf';
import { useExportReportToPptx } from 'components/pages/ReportPage/hooks/useExportReportToPptx/useExportReportToPptx';
import { useFullScreen } from 'components/pages/ReportPage/hooks/useFullScreen';
import { useNarrativeMaps } from 'components/pages/ReportPage/hooks/useNarrativesMaps';
import { useScrollToBottomOfReport } from 'components/pages/ReportPage/hooks/useScrollToBottomOfReport';

export interface ReportPageUrlParams {
    reportId: string;
}

const ResponsiveGridLayout = WidthProvider(Responsive);

const ResponsiveGridLayoutConfig = {
    cols: { lg: 3, md: 3, sm: 3, xs: 3 },
    margin: [25, 25],
    containerPadding: [1, 1],
    isResizable: false,
    rowHeight: 100,
    draggableHandle: '.drag-handle',
};

export const ReportPage = () => {
    // For auto scroll in Vertical edges
    const gridBoxRef = useRef<HTMLDivElement>(null);

    //state
    const [breakpoints, setBreakpoints] = useState<Breakpoints>(() => {
        const width = window.innerWidth;
        return {
            lg: Math.round(width * 0.85),
            md: Math.round(width * 0.7),
            sm: Math.round(width * 0.5),
            xs: Math.round(width * 0.3),
        };
    });

    const reportId = Number(useParams<ReportPageUrlParams>().reportId);
    const { isFullscreen, onFullscreenChange, fullScreenWrapperRef } = useFullScreen();
    const [layout, setLayout] = useState<BreakPointMap | null>(null);
    const [bp, setBp] = useState<ScreenBreakpoints>('lg');
    const [isWidgetsLibraryModalOpen, setIsWidgetsLibraryModalOpen] = useState<boolean>(false);
    const [deleteModal, setDeleteModal] = useState<{ open: boolean; widgetId: number | null }>({
        open: false,
        widgetId: null,
    });
    const [exportProgress, setExportProgress] = useState<{
        isOpen: boolean;
        progress: number;
        exportType?: 'pdf' | 'pptx';
    }>({
        isOpen: false,
        progress: 0,
    });

    //queries
    const { api } = useReportPageProvider({ reportId });
    const queryClient = useQueryClient();
    const {
        data: report,
        isLoading,
        isSuccess: isGetReportSucceed,
        isError: isReportError,
        error: reportResponseError,
    } = api.getReport;
    const { data: projectHierarchyList, isLoading: isLoadingGeneralList } = api.getProgramGeneralHierarchyList;
    const { mutateAsync: deleteWidget } = useMutationDeleteComponent();
    const { mutate: setReport, mutateAsync: setReportAsync } = useMutationSetReport();

    //hooks
    const history = useHistory();
    const { handleSnackBar } = useCustomSnackBar();
    const { sendEventWithDimensions } = useEventWithDimensions();
    const { exportReportToPdf, isLoadingExportReportToPdf } = useExportReportToPdf();
    const { exportReportToPptx, isLoadingExportReportToPptx } = useExportReportToPptx();
    const edgeScroll = useEdgeAutoScroll(gridBoxRef, {
        edgeThreshold: 100,
        maxStep: 20,
        horizontal: false,
        debug: false,
    });
    const { narrativeWidgetsRelationsMap, widgetToChildNarrativeMap } = useNarrativeMaps({
        reportWidgets: report?.components,
    });
    useScrollToBottomOfReport({ layout });

    //logic
    const isNoWidgetsInReport = report?.components?.length === 0;

    //callbacks
    const onAddWidgetToReport = (componentKey: keyof IHashmap): void => {
        const url = AppRoutes.buildPath(
            AppRoutes.DASHBOARD_REPORTING_PATH,
            reportId,
            reportingConfig.confirmWidget.link,
            componentKey,
        );
        history.push(url);
    };

    const onEditWidget = (widgetId: string): void => {
        const url = AppRoutes.buildPath(
            AppRoutes.DASHBOARD_REPORTING_PATH,
            reportId,
            reportingConfig.editWidget.link,
            widgetId,
        );
        history.push(url);
    };

    const handleDragStopped = (afterDropLayout) => {
        if (report && layout) {
            const isLayoutChanged = !sameLayoutArray(afterDropLayout, layout[bp]);
            if (isLayoutChanged) {
                const newLayout = getNewGridLayoutFromSingleLayout(afterDropLayout);
                setLayout(newLayout);
                handleSaveReport(report, newLayout);
            }
        }
    };

    const onDeleteWidget = async (widgetId: string): Promise<void> => {
        const widgetIdNumberToBeDeleted = Number(widgetId);

        // If widget to be deleted have narrative widget(Child), delete it as well
        // else remove only itself
        const narrativeChildrenOfWidgetToBeDeleted = widgetToChildNarrativeMap.get(widgetIdNumberToBeDeleted);
        const idsToRemoveNum = narrativeChildrenOfWidgetToBeDeleted
            ? [widgetIdNumberToBeDeleted, ...Array.from(narrativeChildrenOfWidgetToBeDeleted)]
            : [widgetIdNumberToBeDeleted];

        const idsToRemoveStr = Array.from(new Set(idsToRemoveNum.map(String)));

        try {
            // Delete all widgets in parallel
            await Promise.all(idsToRemoveStr.map((id) => deleteWidget({ componentId: id })));

            // Update local report layout
            const newLayoutItem = layout[bp].filter((li) => !idsToRemoveStr.includes(li.i));
            const newLayout = getNewGridLayoutFromSingleLayout(newLayoutItem);
            setLayout(newLayout);

            // Update backend report layout
            const freshReport = await queryClient.fetchQuery({
                queryKey: ['getReport', { id: Number(reportId) }],
                queryFn: () => getReport({ id: Number(reportId) }),
            });

            const reportPayloadForSetReportApi = prepareReportPayloadForSetReportAPI(freshReport, newLayout);
            await setReportAsync(reportPayloadForSetReportApi);

            // Invalidate cache
            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: ['getReportList'],
                }),
                queryClient.invalidateQueries({
                    queryKey: ['getReport', { id: Number(reportId) }],
                }),
                queryClient.invalidateQueries({
                    queryKey: ['getReportAccessStatus'],
                }),
            ]);

            handleSnackBar(
                idsToRemoveStr.length > 1 ? 'Widgets removed successfully' : 'Widget removed successfully',
                'success',
            );
        } catch (error) {
            DEFAULT_ERROR({ error, handleError: handleSnackBar });
        }
    };

    const handleWidgetSizeChange = (
        widgetId: number,
        widgetSize: WidgetSizes,
        widgetComponentKey: keyof IHashmap,
    ): void => {
        if (report && layout) {
            window.dispatchEvent(new Event('resize'));

            // translate widgetSize to grid size { w, h }
            const newWidgetSize = translateWidgetSize(widgetSize, widgetComponentKey);

            // update widget with new size in the current breakpoint layout
            const modifiedLayout = layout[bp].map((widgetLayout) =>
                widgetLayout.i === String(widgetId) ? { ...widgetLayout, ...newWidgetSize } : widgetLayout,
            );

            // run react grid layout compaction on the current breakpoint layout
            // mainly used to walk down each y and removes gaps (or x if horizontal) to keep correct order of Y's
            const columnsPerBreakpoint = ResponsiveGridLayoutConfig.cols as ColumnsPerBreakpoint;
            const compactedLayout = compactSingleLayout(modifiedLayout, columnsPerBreakpoint[bp], 'vertical');

            const isLayoutChanged = !sameLayoutArray(compactedLayout, layout[bp]);

            if (isLayoutChanged) {
                const newLayout = getNewGridLayoutFromSingleLayout(compactedLayout);
                // set local state
                setLayout(newLayout);
                // save layout to backend
                handleSaveReport(report, newLayout);
            }
        }
    };

    const handleSaveReport = (report: Report, layout: BreakPointMap) => {
        const reportPayloadForSetReportApi = prepareReportPayloadForSetReportAPI(report, layout);
        setReport(reportPayloadForSetReportApi, {
            onSuccess: async () => {
                await queryClient.invalidateQueries({
                    queryKey: ['getReport', { id: Number(reportId) }],
                });
            },
            onError: (error) => {
                DEFAULT_ERROR({ error, handleError: handleSnackBar });
            },
        });
    };

    const handleExportToPdf = async () => {
        if (!report || !layout) {
            return;
        }

        const reportWidgets = report.components ?? [];
        if (!Array.isArray(reportWidgets) || reportWidgets.length === 0) {
            handleSnackBar('Report must have components in order to be exported', 'error');
            return;
        }

        // Show progress modal
        setExportProgress({
            isOpen: true,
            progress: 0,
            exportType: 'pdf',
        });

        try {
            await exportReportToPdf({
                gridRef: gridBoxRef,
                reportName: report.name,
                reportWidgets: reportWidgets,
                options: {
                    onProgress: (progress) => {
                        setExportProgress({
                            isOpen: true,
                            progress,
                            exportType: 'pdf',
                        });
                    },
                },
            });
            handleSnackBar('Report exported to PDF successfully', 'success');
        } catch (error) {
            handleSnackBar('Failed to export report to PDF', 'error');
        } finally {
            // Close modal after a short delay to show completion
            setTimeout(() => {
                setExportProgress({
                    isOpen: false,
                    progress: 0,
                });
            }, 500);
        }
    };

    const handleExportToGridPptx = async () => {
        if (!report || !layout) {
            return;
        }

        const reportWidgets = report.components ?? [];
        if (!Array.isArray(reportWidgets) || reportWidgets.length === 0) {
            handleSnackBar('Report must have components in order to be exported', 'error');
            return;
        }

        // Show progress modal
        setExportProgress({
            isOpen: true,
            progress: 0,
            exportType: 'pptx',
        });

        try {
            await exportReportToPptx({
                reportName: report.name,
                gridContainerRef: gridBoxRef,
                reportWidgets: reportWidgets,
                onProgress: (progress) => {
                    setExportProgress({
                        isOpen: true,
                        progress,
                        exportType: 'pptx',
                    });
                },
            });
            handleSnackBar('Report exported to PPTX successfully', 'success');
        } catch (error) {
            handleSnackBar('Failed to export report to PPTX', 'error');
        } finally {
            // Close modal after a short delay to show completion
            setTimeout(() => {
                setExportProgress({
                    isOpen: false,
                    progress: 0,
                });
            }, 500);
        }
    };

    useEffect(() => {
        if (isGetReportSucceed && report) {
            setLayout(report.gridLayout);
        }
    }, [report, reportId, isGetReportSucceed]);

    useEffect(() => {
        if (report?.name) {
            pageTitleService.set(report.name);
        }
    }, [report?.name]);

    if (isReportError && reportResponseError) {
        if (reportResponseError.status === 403) {
            return (
                <PageContainer>
                    <NoPermissionToViewReport />
                </PageContainer>
            );
        }

        if (!isLoading && !report) {
            return (
                <PageContainer>
                    <ReportNotFound />
                </PageContainer>
            );
        }
    }

    if (isLoading || isLoadingGeneralList) {
        return (
            <PageContainer>
                <OverlayWithSpinner />
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            {(isLoadingExportReportToPdf || isLoadingExportReportToPptx) && <OverlayWithSpinner />}
            {report && layout !== null && (
                <div ref={fullScreenWrapperRef} className={isFullscreen ? classes.fullScreenWrapper : ''}>
                    <HeaderWrapper className={classes.reportStickyHeader}>
                        <BottomHeaderSection>
                            <ReportPageHeader
                                report={report}
                                onAddComponent={() => setIsWidgetsLibraryModalOpen(true)}
                                onFullscreenChange={onFullscreenChange}
                                isFullscreen={isFullscreen}
                                onExportToPdf={handleExportToPdf}
                                onExportToGridPptx={handleExportToGridPptx}
                            />
                        </BottomHeaderSection>
                    </HeaderWrapper>
                    <MainContent>
                        {isNoWidgetsInReport && (
                            <EmptyState onAddComponent={() => setIsWidgetsLibraryModalOpen(true)} />
                        )}
                        <div ref={gridBoxRef} style={{ position: 'relative' }}>
                            <ResponsiveGridLayout
                                className={'layout'}
                                layouts={layout}
                                cols={ResponsiveGridLayoutConfig.cols}
                                rowHeight={ResponsiveGridLayoutConfig.rowHeight}
                                breakpoints={breakpoints}
                                isResizable={ResponsiveGridLayoutConfig.isResizable}
                                onBreakpointChange={(newBp: ScreenBreakpoints) => {
                                    setBp(newBp);
                                }}
                                onDrag={(layout, oldItem, newItem, placeholder, e) => {
                                    edgeScroll.onDrag(e);
                                }}
                                onResizeStop={handleDragStopped}
                                onDragStop={handleDragStopped}
                                draggableHandle={'.drag-handle'}
                                margin={ResponsiveGridLayoutConfig.margin}
                                containerPadding={ResponsiveGridLayoutConfig.containerPadding}
                            >
                                {layout &&
                                    layout[bp].length > 0 &&
                                    report.components.map((component) => {
                                        const componentLayoutItem = layout[bp].find(
                                            (layoutItem) => layoutItem.i === component.id.toString(),
                                        );
                                        if (componentLayoutItem && projectHierarchyList) {
                                            return (
                                                <div key={component.id} data-report-widget-id={component.id}>
                                                    <WidgetInReport
                                                        narrativeWidgetsRelationsMap={narrativeWidgetsRelationsMap}
                                                        widget={component}
                                                        key={component.id}
                                                        onEdit={() => onEditWidget(component.id.toString())}
                                                        onDelete={() =>
                                                            setDeleteModal({
                                                                open: true,
                                                                widgetId: component?.id as number,
                                                            })
                                                        }
                                                        changeWidgetSize={handleWidgetSizeChange}
                                                        projectHierarchyList={projectHierarchyList}
                                                        componentGridSize={{
                                                            h: componentLayoutItem.h,
                                                            w: componentLayoutItem.w,
                                                        }}
                                                        isFullscreen={isFullscreen}
                                                    />
                                                </div>
                                            );
                                        } else {
                                            return null;
                                        }
                                    })}
                            </ResponsiveGridLayout>
                        </div>
                    </MainContent>

                    {isWidgetsLibraryModalOpen && (
                        <WidgetsLibraryModal
                            isModalOpen={isWidgetsLibraryModalOpen}
                            setIsModalOpen={setIsWidgetsLibraryModalOpen}
                            onSubmit={onAddWidgetToReport}
                            isReportHasWidgets={report?.components?.length > 0}
                        />
                    )}
                    <ConfirmDeleteModal
                        open={deleteModal.open}
                        onClose={() =>
                            setDeleteModal({
                                open: false,
                                widgetId: null,
                            })
                        }
                        action={() => {
                            if (deleteModal.widgetId !== null) {
                                // Get component name for tracking
                                const widgetToDelete = report?.components?.find(
                                    (component) => component.id === deleteModal.widgetId,
                                );
                                const componentName =
                                    widgetToDelete && widgetToDelete.componentKey
                                        ? widgetsConfigurations[widgetToDelete.componentKey as keyof IHashmap]?.title ||
                                        'Unknown'
                                        : 'Unknown';

                                // Send tracking event
                                sendEventWithDimensions({
                                    category: 'Reporting',
                                    action: 'Component Deleted',
                                    label: componentName,
                                });

                                onDeleteWidget(deleteModal.widgetId.toString());
                            }
                            setDeleteModal({ open: false, widgetId: null });
                        }}
                        isLoading={isLoading}
                        title={'Delete Component'}
                    >
                        {`Remove component from '${report.name}' report?`}
                    </ConfirmDeleteModal>
                    <ExportProgressModal
                        isOpen={exportProgress.isOpen}
                        progress={exportProgress.progress}
                        exportType={exportProgress.exportType}
                    />
                </div>
            )}
            <ReportCardRoutingModal />
        </PageContainer>
    );
};
